import { Fragment, type FC } from 'react';
import svgPaths from '@/assets/svg-icons';
import { PocNotificationRole } from 'common/enum';
import { PocNotificationEvents, type PocNotificationRoleField } from 'common/constants';

interface Props {
  fullName: string;
  readOnly: boolean;
  selected: boolean;
  resourceRequestRole: Nullable<PocNotificationRoleValue>;
  patientTransferRole: Nullable<PocNotificationRoleValue>;
  inventoryRole: Nullable<PocNotificationRoleValue>;
  bedCountRole: Nullable<PocNotificationRoleValue>;
  onClick?: () => void;
  onRemove?: () => void;
}

type PocRoles = Record<PocNotificationRoleField, Nullable<PocNotificationRoleValue>>;
type EventsByRole = Record<PocNotificationRoleValue, string[]>;

/*
 * Rank order, deliberately not `PocNotificationRoleOptions`' order. That constant is the
 * contact modal's dropdown, which reads Notify first; the card ranks the designations, so
 * Primary leads. The headings are the card's own copy for the same reason -- these are two
 * independent surfaces that happen to share three words today, and renaming a dropdown option
 * should not silently rename a card heading.
 *
 * The Figma node (`2417-10284`) draws Primary and Secondary only. The third group is a
 * deliberate deviation: without it a contact holding nothing but NOTIFY designations holds no
 * renderable group at all, falls through to the all-empty case, and reads "None / None" --
 * identical to a contact who receives nothing.
 */
const ROLE_GROUPS: { role: PocNotificationRoleValue; heading: string }[] = [
  { role: PocNotificationRole.PRIMARY, heading: 'Primary' },
  { role: PocNotificationRole.SECONDARY, heading: 'Secondary' },
  { role: PocNotificationRole.NOTIFY, heading: 'Notify' },
];

/*
 * Invert the per-event roles into a role-to-events map.
 *
 *   stored per contact            rendered per card
 *   ─────────────────────         ──────────────────────────────
 *   resourceRequest  SECONDARY    PRIMARY    []          <- empty, so not rendered
 *   patientTransfer  SECONDARY    SECONDARY  ['Resource request', 'Patient transfer']
 *   inventory        null         NOTIFY     []          <- empty, so not rendered
 *   bedCount         null
 *
 * One pass over `PocNotificationEvents` rather than three filters over it, so a fifth event or
 * a fourth role costs nothing here. Events keep that constant's order inside a group, which is
 * the order their rows sit in in the contact modal.
 *
 *   happy: an event with a recognised role appends its label to that role's list
 *   nil/null: an event with no role joins no group -- that role is dropped from the render
 *   empty/zero: a contact with no roles at all leaves all three lists empty, the one case that
 *     falls back to rendering every role as "None" -- see the fallback at the call site
 *   upstream error: a role outside the enum is dropped rather than opening a fourth group.
 *     `normalizePocRoles` (src/lib/pocFormData.ts) already narrows API data on the way into
 *     form state; the `in` test below is the render-side backstop, because nothing
 *     schema-checks what reaches these props and the type says it cannot happen.
 *
 * A fourth role added to `PocNotificationRole` fails to compile here -- `EventsByRole` would
 * demand a bucket this literal does not have. That compile error is the tripwire that lands a
 * developer in this file, where `ROLE_GROUPS` above needs the matching heading.
 */
function groupEventsByRole(roles: PocRoles): EventsByRole {
  const grouped: EventsByRole = {
    [PocNotificationRole.PRIMARY]: [],
    [PocNotificationRole.SECONDARY]: [],
    [PocNotificationRole.NOTIFY]: [],
  };

  PocNotificationEvents.forEach(({ label, roleField }) => {
    const role = roles[roleField];
    if (role && role in grouped) grouped[role].push(label);
  });

  return grouped;
}

const CardPoc: FC<Props> = ({
  bedCountRole,
  fullName,
  inventoryRole,
  onClick,
  onRemove,
  patientTransferRole,
  readOnly,
  resourceRequestRole,
  selected,
}) => {
  const eventsByRole = groupEventsByRole({
    resourceRequestRole,
    patientTransferRole,
    inventoryRole,
    bedCountRole,
  });

  // Only the roles this contact actually holds. A contact who holds nothing has nothing to
  // drop, so it falls back to the full set and every heading reads "None" -- rendering an
  // empty region instead would read as a broken card rather than as "receives nothing".
  const held = ROLE_GROUPS.filter(({ role }) => eventsByRole[role].length > 0);
  const groups = held.length > 0 ? held : ROLE_GROUPS;

  /*
   * `fullName` arrives already joined and trimmed from two `|| ''` fallbacks, so a contact
   * holding neither name reaches here as an empty string. Both controls below take their
   * accessible name from it: an empty one leaves the name button announcing nothing at all and
   * the remove button announcing a bare "Remove", which is the same on every card.
   *
   * `buildPocHolderMap` already refuses to name a contact it cannot name (`pocFormData.ts`, the
   * `if (!realName) return;` guard) -- nameless records are a shape this codebase expects from an
   * ambient, unchecked API response rather than a hypothetical. Naming the gap is better than
   * rendering a control nobody can identify: the card still draws, and the roles beneath it still
   * say what the contact receives.
   */
  const displayName = fullName || 'Unnamed contact';

  return (
    // The card fills the cell it is given rather than pinning itself to the design's 320px: the
    // list in `OrgInfo` lays the cards on a two-column grid, so the column track decides how
    // wide a card is and the card only has to avoid overflowing it. `min-w-0` is what keeps
    // that true -- a grid item's automatic minimum size is its content, so without it the
    // truncating name row below pushes the card past its track. That overhang is what made the
    // list scroll sideways at a 360px viewport, parking the remove button off-screen.
    <div
      className={`hc:w-full ${selected ? 'hc:bg-[#f0f3ff] hc:dark:bg-muted' : 'hc:bg-card hc:hover:bg-[#f8f9fd] hc:dark:hover:bg-card/50'} hc:min-h-16.25 hc:relative hc:rounded-md hc:min-w-0 hc:cursor-pointer hc:transition-all hc:duration-200 hc:ease-in-out hc:hover:shadow-md hc:hover:scale-101 hc:hover:z-10`}>
      <div
        className={`hc:absolute hc:border ${selected ? 'hc:border-stroke-accent-bold' : 'hc:border-[#d5d9e2] hc:dark:border-border'} ${!selected && 'hc:hover:border-[#a3afc8]'} hc:rounded-md hc:duration-200 hc:pointer-events-none hc:inset-0 hc:border-solid hc:transition-colors`}
      />
      {/* One column, so the icon sits on its own row above the name and every row starts at the
          same left edge. The icon used to lead a row with the name beside it, which set the
          card's left edge 30px in from the padding for everything below it -- the name kept a
          `w-5` spacer under the icon to line up with, and the groups had to opt out of the
          indent to get their width back. With the icon on its own row there is no indent to
          line up with or opt out of. */}
      <div className='hc:relative hc:box-border hc:flex hc:size-full hc:min-h-16.25 hc:flex-col hc:items-start hc:gap-1 hc:py-1 hc:pr-1 hc:pl-3'>
        <div
          className='hc:flex hc:w-full hc:min-w-0 hc:flex-col hc:items-start hc:justify-start hc:gap-1'
          onClick={onClick}>
          <div className='hc:relative hc:shrink-0 hc:size-5'>
            <svg
              className='hc:block hc:size-full'
              fill='none'
              preserveAspectRatio='none'
              viewBox='0 0 20 20'>
              <g id={selected ? 'ic:baseline-check-box' : 'ic:round-branding-watermark'}>
                <path
                  d={selected ? svgPaths.checked : svgPaths.person}
                  fill={selected ? 'var(--fill-0, #0949EE)' : 'var(--fill-0, #B1BBC8)'}
                  id='Vector'
                />
              </g>
            </svg>
          </div>
          {/* `min-w-0` on a full-width block: `truncate` needs an element that is allowed to
              narrow below its text, and a flex child will not do that on its own. It matters
              here even though the name now owns the whole row, because the row is only as wide
              as the grid column the card landed in. */}
          <div
            className={`hc:w-full hc:min-w-0 hc:font-['Roboto:Medium',sans-serif] hc:font-medium ${selected ? 'hc:text-primary' : 'hc:text-typography-secondary hc:dark:text-foreground'} hc:text-left hc:text-sm`}
            style={{ fontVariationSettings: "'wdth' 100" }}>
            {/* The name is the card's keyboard entry point. The column around it keeps its own
                `onClick` so a mouse can still hit anywhere on the card, but only phrasing
                content may sit inside a `button` -- the definition list below rules out
                wrapping the column itself, and `role='button'` there would flatten the roles
                and events out of the accessible name. `stopPropagation` so a click on the name
                does not also bubble into that column handler and select the contact twice. */}
            <button
              type='button'
              className='hc:block hc:w-full hc:min-w-0 hc:cursor-pointer hc:truncate hc:text-left hc:leading-normal'
              onClick={(event) => {
                event.stopPropagation();
                onClick?.();
              }}>
              {displayName}
            </button>
          </div>
          {/* A definition list, not a stack of divs: each heading labels the events beneath it,
              which is what `dt`/`dd` mean.

              Two columns, so a third held role wraps onto a second row. The count is set by the
              narrow end of the card's range, not the wide one: below the list's `md` breakpoint
              a card is a full single column, roughly 320px at a 360px viewport, where three
              columns give each one 96px and split "Resource request" across two lines. Two
              columns hold ~148px there and every event name fits whole; above the breakpoint
              they simply run wider. `min-w-0` is what lets a longer name wrap inside its column
              rather than forcing the row wider.

              A contact holding exactly one role takes the whole row instead of leaving the
              second column empty, which keeps its event list on fewer lines. */}
          <dl className='hc:grid hc:w-full hc:grid-cols-2 hc:items-start hc:gap-x-2 hc:gap-y-1'>
            {groups.map(({ role, heading }) => {
              const events = eventsByRole[role];

              return (
                <div
                  key={role}
                  className={`hc:flex hc:min-w-0 hc:flex-col ${groups.length === 1 ? 'hc:col-span-2' : ''}`}>
                  <dt className='hc:text-typography-label hc:dark:text-muted-foreground hc:text-xs hc:leading-normal'>
                    {heading}
                  </dt>
                  <dd className='hc:text-card-foreground hc:text-sm hc:leading-normal hc:font-semibold hc:wrap-break-word'>
                    {events.length === 0
                      ? 'None'
                      : events.map((label, index) => (
                          <Fragment key={label}>
                            {/* The separator is its own element so it can carry the design's
                                `opacity-20` without dimming the names. */}
                            {index > 0 && <span className='hc:opacity-20'> / </span>}
                            {label}
                          </Fragment>
                        ))}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
        {/* A sibling of the clickable column rather than a child: a click on remove would
            otherwise bubble into `onClick` and select the contact it just deleted. `top-1`
            matches the container's `py-1`, which lines the button up with the icon row.

            Placed after that column rather than before it, which is a focus-order decision, not
            a visual one -- `absolute` pins it top-right either way. Leading meant every card put
            its remove control ahead of its own name in the tab order, so reaching the eighth
            contact in a list meant passing eight destructive controls first, and `onDeletePOC`
            marks a contact deleted on the spot with nothing to confirm. Trailing keeps the
            sibling relationship the bubbling note above depends on. */}
        {!readOnly && (
          <button
            type='button'
            aria-label={`Remove ${displayName}`}
            className='hc:absolute hc:top-1 hc:right-1 hc:z-10 hc:size-5 hc:cursor-pointer'
            onClick={onRemove}>
            <svg
              className='hc:block hc:size-full'
              fill='none'
              preserveAspectRatio='none'
              viewBox='0 0 20 20'>
              <g id={'ic:round-branding-watermark'}>
                <path d={svgPaths.close} fill={'var(--fill-0, #3E454C)'} id='Vector' />
              </g>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};
export default CardPoc;
